import {
  BaseTextFieldProps,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import React from "react"
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from "react-international-phone"

export interface FieldPhoneProps extends Omit<BaseTextFieldProps, "error"> {
  fieldName: string
  value: string
  disabled?: boolean
  error: string | undefined
  isReadOnly?: boolean
  defaultCountry?: CountryIso2
  onChange?: (phone: string) => void
  sx?: any
}

export const FieldPhone: React.FC<FieldPhoneProps> = ({
  fieldName,
  value,
  disabled,
  isReadOnly,
  onChange,
  error,
  sx,
  defaultCountry,
  ...restProps
}) => {
  const timeout = React.useRef<any>(undefined)
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: defaultCountry || "us",
    value,
    countries: defaultCountries,
    onChange: data => {
      if (onChange) {
        onChange(data.phone.replace(" /g", ""))
      }
    }
  })
  return (
    <Field name={fieldName}>
      {({ form: { touched, setFieldValue } }: FieldProps) => {
        const isTouched: boolean = hasBeenTouched(touched, fieldName)
        const isDisabled: boolean = disabled || Boolean(isReadOnly)
        const hasError = isTouched ? !!error : false
        return (
          <FormControl
            id={fieldName}
            error={hasError}
            sx={{
              "position": "relative",
              "& .icon": {
                position: "absolute",
                top: ".7rem",
                right: ".4rem",
                cursor: "pointer",
                color: theme => theme.palette.text.secondary
              },
              "& .icon:hover": {
                color: theme => theme.palette.text.primary
              },
              ...sx
            }}>
            <TextField
              id={`${fieldName}-text-field`}
              variant="outlined"
              label={translate("label.phone")}
              size="small"
              color="primary"
              disabled={isDisabled}
              placeholder="Phone number"
              value={inputValue}
              onChange={event => {
                checkTouched(touched, fieldName)
                handlePhoneValueChange(event)
                clearTimeout(timeout.current)
                timeout.current = setTimeout(() => setFieldValue(fieldName, event.target.value.replace(" /g", "")), 500)
              }}
              type="tel"
              onBlur={() => {
                if (!isTouched) {
                  checkTouched(touched, fieldName)
                  setFieldValue(fieldName, inputValue)
                }
              }}
              inputRef={inputRef}
              error={hasError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" style={{ marginRight: "2px", marginLeft: "-8px" }}>
                    <Select
                      MenuProps={{
                        style: {
                          height: "300px",
                          width: "360px",
                          top: "10px",
                          left: "-34px"
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left"
                        }
                      }}
                      sx={{
                        "width": "max-content",
                        // Remove default outline (display only on focus)
                        "fieldset": {
                          display: "none"
                        },
                        '&.Mui-focused:has(div[aria-expanded="false"])': {
                          fieldset: {
                            display: "block"
                          }
                        },
                        // Update default spacing
                        ".MuiSelect-select": {
                          padding: "8px",
                          paddingRight: "24px !important"
                        },
                        "svg": {
                          right: 0
                        }
                      }}
                      value={country.iso2}
                      onChange={e => setCountry(e.target.value as CountryIso2)}
                      renderValue={value => <FlagImage iso2={value} style={{ display: "flex" }} />}>
                      {defaultCountries.map(c => {
                        const country = parseCountry(c)
                        return (
                          <MenuItem key={country.iso2} value={country.iso2}>
                            <FlagImage iso2={country.iso2} style={{ marginRight: "8px" }} />
                            <Typography marginRight="8px">{country.name}</Typography>
                            <Typography color="gray">+{country.dialCode}</Typography>
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </InputAdornment>
                )
              }}
              {...restProps}
            />
            {hasError && <FormHelperText id={`${fieldName}-error`}>{error}</FormHelperText>}
          </FormControl>
        )
      }}
    </Field>
  )
}
